// src/utils/tradeWindows.js

import {
  calcEntryVsWindowOpen,
  toUTCDate,
  pickBuyPrice,
} from "./smartTimingImpact";

export const fmtUsd2 = (n) =>
  Number(n || 0).toLocaleString(
    undefined,
    {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }
  );

export const fmtSats0 = (n) =>
  Math.round(
    Number(n || 0)
  ).toLocaleString(
    undefined,
    {
      maximumFractionDigits: 0,
    }
  );

export const fmtPrice0 = (n) =>
  Number(n || 0).toLocaleString(
    undefined,
    {
      maximumFractionDigits: 0,
    }
  );

const userTZ =
  Intl.DateTimeFormat()
    .resolvedOptions()
    .timeZone;

const fmtWindowDate = (
  value
) =>
  new Intl.DateTimeFormat(
    undefined,
    {
      timeZone: userTZ,
      month: "short",
      day: "numeric",
    }
  ).format(
    toUTCDate(value)
  );

export const fmtWindowLabel = (start, end = null) => {
  const startDate = toUTCDate(start);
  const endDate = toUTCDate(end);
  if (!Number.isFinite(startDate.getTime())) return "Buy window";
  const startLabel = fmtWindowDate(start);
  if (!end || !Number.isFinite(endDate.getTime())) return `${startLabel} window`;

  const monthYear = new Intl.DateTimeFormat(undefined, {
    timeZone: userTZ,
    year: "numeric",
    month: "numeric",
  });
  const sameMonth = monthYear.format(startDate) === monthYear.format(endDate);
  const endLabel = sameMonth
    ? new Intl.DateTimeFormat(undefined, { timeZone: userTZ, day: "numeric" }).format(endDate)
    : fmtWindowDate(end);
  return `${startLabel}–${endLabel} window`;
};

const fmtWindowDateTime = (
  value
) =>
  new Intl.DateTimeFormat(
    undefined,
    {
      timeZone: userTZ,
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }
  ).format(
    toUTCDate(value)
  );

function tradeWindowKey(t) {
  return (
    t.window_start ||
    t.windowStart ||
    t.window_start_at ||
    t.windowStartAt ||
    t.buy_window_start ||
    t.buyWindowStart ||
    t.window_open_at ||
    t.windowOpenAt ||
    t.window_opened_at ||
    t.windowOpenedAt ||
    null
  );
}

export function groupTradesByWindow(
  trades = []
) {
  const map = new Map();

  for (const t of trades || []) {
    const rawWindowStart =
      tradeWindowKey(t);

    if (
      !rawWindowStart &&
      import.meta.env.DEV
    ) {
      console.warn(
        "Trade is missing window_start; grouping will fall back to executed_at",
        t
      );
    }

    const rawKey =
      rawWindowStart ||
      t.executed_at;

    const keyDate = rawKey
      ? toUTCDate(rawKey)
      : toUTCDate(
          t.executed_at
        );

    const windowKey =
      keyDate?.toISOString?.() ||
      String(
        rawKey ||
        t.executed_at
      );

    const planKey =
      t.plan_id != null
        ? String(t.plan_id)
        : "no-plan";

    const key =
      `${planKey}:${windowKey}`;

    if (!map.has(key)) {
      map.set(
        key,
        {
          key,

          planId:
            t.plan_id ??
            null,

          windowStart:
            rawWindowStart ||
            t.executed_at,

          hasRealWindowStart:
            !!rawWindowStart,

          label:
            `${fmtWindowDate(
              rawWindowStart ||
              t.executed_at
            )} window`,

          trades: [],

          totalUsd: 0,
          totalFee: 0,
          totalBtc: 0,
          totalSats: 0,

          weightedPriceTotal: 0,
          weightedPriceBtc: 0,

          firstExecutedAt:
            t.executed_at,

          lastExecutedAt:
            t.executed_at,

          isSmartTiming: false,
          windowOpenPrice: null,
          entryPriceDataComplete: true,
          validEntryTradeCount: 0,
          windowEnd: null,
          windowEndSource: null,
        }
      );
    }

    const g =
      map.get(key);

    const btcAmt =
      parseFloat(
        t.btc_amt || 0
      );

    const grossUSD =
      parseFloat(
        t.usd_cost || 0
      );

    const feeUSD =
      parseFloat(
        t.fee || 0
      );

    const buyPx =
      pickBuyPrice(t);

    const entry =
      calcEntryVsWindowOpen(t);

    const enriched = {
      ...t,

      _btcAmt:
        btcAmt,

      _grossUSD:
        grossUSD,

      _feeUSD:
        feeUSD,

      _buyPx:
        buyPx,

      _sats:
        Number.isFinite(
          btcAmt
        ) &&
        btcAmt > 0
          ? btcAmt * 1e8
          : 0,

      // Entry context only.
      // This is NOT final Smart Timing performance.
      _entryPct:
        entry &&
        Number.isFinite(
          entry.entryPct
        )
          ? entry.entryPct
          : null,
    };

    g.trades.push(
      enriched
    );

    g.totalUsd +=
      Number.isFinite(
        grossUSD
      )
        ? grossUSD
        : 0;

    g.totalFee +=
      Number.isFinite(
        feeUSD
      )
        ? feeUSD
        : 0;

    if (t?.smart_timing === true) {
      g.isSmartTiming = true;
      const startPx = Number(t.window_open_price);
      if (!(startPx > 0 && Number.isFinite(buyPx) && buyPx > 0 && btcAmt > 0)) {
        g.entryPriceDataComplete = false;
      } else {
        g.validEntryTradeCount += 1;
        if (g.windowOpenPrice == null) {
          g.windowOpenPrice = startPx;
        } else if (Math.abs(startPx - g.windowOpenPrice) / g.windowOpenPrice > 0.001) {
          // Legacy rows with incompatible opening-price snapshots should
          // not produce a potentially misleading aggregate entry comparison.
          g.entryPriceDataComplete = false;
        }
      }
    } else {
      // A mixed historical group cannot support a Smart Timing entry estimate.
      g.entryPriceDataComplete = false;
    }

    if (
      Number.isFinite(
        btcAmt
      ) &&
      btcAmt > 0
    ) {
      g.totalBtc +=
        btcAmt;

      g.totalSats +=
        btcAmt * 1e8;

      if (
        Number.isFinite(
          buyPx
        ) &&
        buyPx > 0
      ) {
        g.weightedPriceTotal +=
          buyPx * btcAmt;

        g.weightedPriceBtc +=
          btcAmt;
      }
    }

    if (
      toUTCDate(
        t.executed_at
      ) <
      toUTCDate(
        g.firstExecutedAt
      )
    ) {
      g.firstExecutedAt =
        t.executed_at;
    }

    if (
      toUTCDate(
        t.executed_at
      ) >
      toUTCDate(
        g.lastExecutedAt
      )
    ) {
      g.lastExecutedAt =
        t.executed_at;
    }
  }

  const groups = Array.from(
    map.values()
  )
    .map((g) => {
      g.trades.sort(
        (a, b) =>
          toUTCDate(
            a.executed_at
          ) -
          toUTCDate(
            b.executed_at
          )
      );

      g.avgBuyPrice =
        g.weightedPriceBtc > 0
          ? (
              g.weightedPriceTotal /
              g.weightedPriceBtc
            )
          : null;

      // Not every tranche is necessarily dip-triggered; some fill at a deadline.
      g.executionLabel =
        g.trades.length === 1
          ? "1 buy"
          : `${g.trades.length} buys in window`;

      g.entryPct =
        g.isSmartTiming &&
        g.entryPriceDataComplete &&
        g.validEntryTradeCount === g.trades.length &&
        g.windowOpenPrice > 0 &&
        Number.isFinite(g.avgBuyPrice) &&
        Math.abs(g.weightedPriceBtc - g.totalBtc) < 1e-8
          ? ((g.windowOpenPrice - g.avgBuyPrice) / g.windowOpenPrice) * 100
          : null;

      g.rangeLabel =
        g.trades.length <= 1
          ? fmtWindowDateTime(
              g.trades[0]
                ?.executed_at ||
              g.windowStart
            )
          : (
              `${fmtWindowDateTime(
                g.firstExecutedAt
              )} – ` +
              `${fmtWindowDateTime(
                g.lastExecutedAt
              )}`
            );

      return g;
    })
    .sort(
      (a, b) =>
        toUTCDate(b.windowStart) - toUTCDate(a.windowStart)
    );

  // Label known historical windows using observed cadence, not the plan's
  // mutable *current* frequency. The backend has the authoritative end for
  // completed windows, which Trades.jsx will use when it has a result.
  const byPlan = new Map();
  for (const group of groups) {
    if (!group.hasRealWindowStart || group.planId == null) continue;
    const key = String(group.planId);
    if (!byPlan.has(key)) byPlan.set(key, []);
    byPlan.get(key).push(group);
  }
  const cadenceMs = [1, 7, 14].map((days) => days * 86400000);
  const inferDuration = (start, end) => {
    if (!start || !end) return null;
    const a = toUTCDate(start).getTime();
    const b = toUTCDate(end).getTime();
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    const diff = b - a;
    return cadenceMs.find((ms) => Math.abs(diff - ms) <= 5 * 60000) || null;
  };

  for (const history of byPlan.values()) {
    history.sort((a, b) => toUTCDate(a.windowStart) - toUTCDate(b.windowStart));
    history.forEach((group, index) => {
      const prior = history[index - 1];
      const following = history[index + 1];
      const prevMs = prior && inferDuration(prior.windowStart, group.windowStart);
      const nextMs = following && inferDuration(group.windowStart, following.windowStart);
      // A transition is ambiguous without a historical cadence snapshot.
      const duration = prevMs && nextMs && prevMs !== nextMs
        ? null
        : (nextMs || prevMs || null);
      if (duration) {
        const startMs = toUTCDate(group.windowStart).getTime();
        group.windowEnd = new Date(startMs + duration).toISOString();
        group.windowEndSource = nextMs ? "observed_next_cadence" : "observed_previous_cadence";
        group.label = fmtWindowLabel(group.windowStart, group.windowEnd);
      }
    });
  }

  return groups;
}